import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { API_BASE_URL } from '../utils/api'
import { PlusIcon, EditIcon, TrashIcon, XIcon, SaveIcon } from './Icons'

interface DealStatus {
  id: number
  name: string
  name_fr?: string
  color: string
  position: number
}

interface StatusRule {
  id: number
  status_id: number
  name: string
  description?: string
  trigger_type: 'on_enter' | 'on_exit'
  required_fields?: string[]
  custom_validation?: string
  validation_message?: string
  automation_type?: string
  automation_config?: any
  is_active: boolean
  priority: number
}

interface StatusRulesSectionProps {
  statuses: DealStatus[]
  onError: (message: string) => void
  onSuccess: (message: string) => void
}

// All real deal fields that can be required
const AVAILABLE_FIELDS: { key: string; label: string; description: string; deprecated?: boolean }[] = [
  { key: 'amount', label: 'Deal Amount', description: 'The mortgage amount' },
  { key: 'customer_id', label: 'Client', description: 'A client must be linked to the deal' },
  { key: 'lender_id', label: 'Lender', description: 'A lender must be selected' },
  { key: 'deal_type_id', label: 'Deal Type', description: 'The type of mortgage deal' },
  { key: 'closing_date', label: 'Closing Date', description: 'The expected closing date' },
  { key: 'description', label: 'Notes', description: 'Deal notes or description' },
  { key: 'base_points', label: 'Base Points', description: 'Commission base points' },
  { key: 'bonus_points', label: 'Bonus Points', description: 'Commission bonus points' },
  { key: 'volume_points', label: 'Volume Points', description: 'Commission volume points' },
  // Kept only so existing rules can be unticked — field has been removed from the app
  { key: 'amount_financed', label: 'Amount Financed', description: 'Removed — uncheck to clean up', deprecated: true },
]

const StatusRulesSection: React.FC<StatusRulesSectionProps> = ({ statuses, onError, onSuccess }) => {
  const { t } = useTranslation()
  const [selectedStatus, setSelectedStatus] = useState<DealStatus | null>(null)

  // Settings panel state
  const [requiredFields, setRequiredFields] = useState<string[]>([])
  const [savingFields, setSavingFields] = useState(false)

  // Rules modal state
  const [showRulesModal, setShowRulesModal] = useState(false)
  const [rules, setRules] = useState<StatusRule[]>([])
  const [rulesLoading, setRulesLoading] = useState(false)
  const [showCreateRule, setShowCreateRule] = useState(false)
  const [editingRule, setEditingRule] = useState<StatusRule | null>(null)

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    trigger_type: 'on_enter' as 'on_enter' | 'on_exit',
    required_fields: [] as string[],
    custom_validation: '',
    validation_message: '',
    automation_type: '',
    automation_config: {},
    is_active: true,
    priority: 0
  })

  const customValidations = [
    { value: '', label: 'None' },
    { value: 'funding_modal', label: 'Funding Confirmation Modal' },
    { value: 'compliance_check', label: 'Compliance Check' }
  ]

  const automationTypes = [
    { value: '', label: 'None' },
    { value: 'send_sms', label: 'Send SMS' },
    { value: 'create_task', label: 'Create Task' },
    { value: 'send_email', label: 'Send Email' }
  ]

  // Load required fields whenever selected status changes
  useEffect(() => {
    if (selectedStatus) {
      fetchRequiredFields(selectedStatus.id)
    } else {
      setRequiredFields([])
    }
  }, [selectedStatus])

  // Load rules when the rules modal opens
  useEffect(() => {
    if (showRulesModal && selectedStatus) {
      fetchRulesForStatus(selectedStatus.id)
    }
  }, [showRulesModal, selectedStatus])

  const fetchRequiredFields = async (statusId: number) => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/api/deal-status-rules/status/${statusId}/required-fields`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (response.ok) {
        const data = await response.json()
        setRequiredFields(data.required_fields || [])
      }
    } catch (error) {
      console.error('Error fetching required fields:', error)
    }
  }

  const fetchRulesForStatus = async (statusId: number) => {
    try {
      setRulesLoading(true)
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/api/deal-status-rules/status/${statusId}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (response.ok) {
        const data = await response.json()
        // Filter out the auto-managed __required_fields__ rule — that's managed via the settings panel
        setRules((data.rules || []).filter((r: StatusRule) => r.name !== '__required_fields__'))
      }
    } catch (error) {
      console.error('Error fetching status rules:', error)
    } finally {
      setRulesLoading(false)
    }
  }

  /**
   * Save required fields for a specific status.
   * Returns true on success.
   */
  const saveRequiredFieldsForStatus = useCallback(async (statusId: number, fields: string[]): Promise<boolean> => {
    const token = localStorage.getItem('token')
    const response = await fetch(`${API_BASE_URL}/api/deal-status-rules/status/${statusId}/required-fields`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ required_fields: fields })
    })
    return response.ok
  }, [])

  /**
   * Toggle a field on/off for the selected status with cascade rules:
   *
   * Turning ON  → cascade FORWARD:  status N requires the field, so every status
   *               after N (higher position) must also require it. No gaps allowed.
   *
   * Turning OFF → cascade BACKWARD: removing the field from status N means every
   *               status BEFORE N (lower position) must also drop it, because you
   *               cannot enforce a field at an earlier stage if a later stage
   *               doesn't require it.
   */
  const handleToggleField = async (fieldKey: string) => {
    if (!selectedStatus) return

    const isCurrentlyOn = requiredFields.includes(fieldKey)
    const updatedFieldsForCurrent = isCurrentlyOn
      ? requiredFields.filter(f => f !== fieldKey)
      : [...requiredFields, fieldKey]

    // Optimistically update UI for the current status
    setRequiredFields(updatedFieldsForCurrent)
    setSavingFields(true)

    // Cascade targets depend on direction
    const cascadeStatuses = isCurrentlyOn
      ? statuses.filter(s => s.position < selectedStatus.position) // OFF → backward
      : statuses.filter(s => s.position > selectedStatus.position) // ON  → forward

    const fieldLabel = AVAILABLE_FIELDS.find(f => f.key === fieldKey)?.label || fieldKey

    try {
      // Save the current status first
      const promises: Promise<boolean>[] = [
        saveRequiredFieldsForStatus(selectedStatus.id, updatedFieldsForCurrent)
      ]

      // Cascade: fetch each affected status's current fields and add/remove the field
      for (const target of cascadeStatuses) {
        promises.push(
          (async () => {
            const token = localStorage.getItem('token')
            const resp = await fetch(
              `${API_BASE_URL}/api/deal-status-rules/status/${target.id}/required-fields`,
              { headers: { Authorization: `Bearer ${token}` } }
            )
            if (!resp.ok) return false
            const data = await resp.json()
            const existing: string[] = data.required_fields || []

            if (!isCurrentlyOn) {
              // Turning ON: add the field if not already present
              if (existing.includes(fieldKey)) return true
              return saveRequiredFieldsForStatus(target.id, [...existing, fieldKey])
            } else {
              // Turning OFF: remove the field if present
              if (!existing.includes(fieldKey)) return true
              return saveRequiredFieldsForStatus(target.id, existing.filter(f => f !== fieldKey))
            }
          })()
        )
      }

      const results = await Promise.all(promises)
      if (results.every(Boolean)) {
        const cascadeCount = cascadeStatuses.length
        if (isCurrentlyOn) {
          onSuccess(
            `"${fieldLabel}" is no longer required${
              cascadeCount > 0 ? ` (also removed from ${cascadeCount} earlier status${cascadeCount !== 1 ? 'es' : ''})` : ''
            }`
          )
        } else {
          onSuccess(
            `"${fieldLabel}" is now required${
              cascadeCount > 0 ? ` (also applied to ${cascadeCount} later status${cascadeCount !== 1 ? 'es' : ''})` : ''
            }`
          )
        }
      } else {
        onError('Some statuses could not be updated')
        setRequiredFields(requiredFields) // revert optimistic update
      }
    } catch {
      onError('Error saving required fields')
      setRequiredFields(requiredFields)
    } finally {
      setSavingFields(false)
    }
  }

  // ── Rules CRUD ──────────────────────────────────────────────────────────────

  const handleCreateRule = async () => {
    if (!selectedStatus) return
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/api/deal-status-rules`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status_id: selectedStatus.id, ...formData })
      })
      if (response.ok) {
        onSuccess('Rule created successfully')
        setShowCreateRule(false)
        resetForm()
        fetchRulesForStatus(selectedStatus.id)
      } else {
        onError('Failed to create rule')
      }
    } catch {
      onError('Error creating rule')
    }
  }

  const handleUpdateRule = async () => {
    if (!editingRule) return
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/api/deal-status-rules/${editingRule.id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      })
      if (response.ok) {
        onSuccess('Rule updated successfully')
        setEditingRule(null)
        resetForm()
        if (selectedStatus) fetchRulesForStatus(selectedStatus.id)
      } else {
        onError('Failed to update rule')
      }
    } catch {
      onError('Error updating rule')
    }
  }

  const handleDeleteRule = async (ruleId: number) => {
    if (!confirm('Are you sure you want to delete this rule?')) return
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/api/deal-status-rules/${ruleId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      })
      if (response.ok) {
        onSuccess('Rule deleted successfully')
        if (selectedStatus) fetchRulesForStatus(selectedStatus.id)
      } else {
        onError('Failed to delete rule')
      }
    } catch {
      onError('Error deleting rule')
    }
  }

  const handleToggleRule = async (rule: StatusRule) => {
    try {
      const token = localStorage.getItem('token')
      await fetch(`${API_BASE_URL}/api/deal-status-rules/${rule.id}/toggle`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      })
      if (selectedStatus) fetchRulesForStatus(selectedStatus.id)
    } catch {
      onError('Error toggling rule')
    }
  }

  const openEditRule = (rule: StatusRule) => {
    setEditingRule(rule)
    setFormData({
      name: rule.name,
      description: rule.description || '',
      trigger_type: rule.trigger_type,
      required_fields: rule.required_fields || [],
      custom_validation: rule.custom_validation || '',
      validation_message: rule.validation_message || '',
      automation_type: rule.automation_type || '',
      automation_config: rule.automation_config || {},
      is_active: rule.is_active,
      priority: rule.priority
    })
    setShowCreateRule(true)
  }

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      trigger_type: 'on_enter',
      required_fields: [],
      custom_validation: '',
      validation_message: '',
      automation_type: '',
      automation_config: {},
      is_active: true,
      priority: 0
    })
  }

  const toggleFormField = (field: string) => {
    setFormData(prev => ({
      ...prev,
      required_fields: prev.required_fields.includes(field)
        ? prev.required_fields.filter(f => f !== field)
        : [...prev.required_fields, field]
    }))
  }

  return (
    <div className="mt-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-xl p-6 mb-6">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
          Deal Status Settings
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Configure validation and automation for each deal status
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-4">
        {/* Status List */}
        <div className="lg:w-72 flex-shrink-0">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">Deal Statuses</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Select a status to configure</p>
            </div>
            <div className="p-3 max-h-[520px] overflow-y-auto">
              {statuses.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">No statuses found</p>
              ) : (
                <div className="space-y-1.5">
                  {statuses.map((status) => {
                    // Count how many fields are required for this status (just visual hint)
                    const isSelected = selectedStatus?.id === status.id
                    return (
                      <div
                        key={status.id}
                        onClick={() => setSelectedStatus(status)}
                        className={`p-3 rounded-lg border cursor-pointer transition-all ${
                          isSelected
                            ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                            : 'border-gray-200 dark:border-gray-600 hover:border-purple-300 dark:hover:border-purple-500'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2.5">
                            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: status.color }} />
                            <span className="text-sm font-medium text-gray-900 dark:text-white">{status.name}</span>
                          </div>
                          {isSelected && (
                            <div className="w-1.5 h-1.5 rounded-full bg-purple-500" />
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Settings Panel */}
        <div className="flex-1">
          {selectedStatus ? (
            <div className="space-y-4">
              {/* Status header */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 px-5 py-4 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: selectedStatus.color }} />
                  <div>
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white">{selectedStatus.name}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Status settings</p>
                  </div>
                </div>
                {/* Rules button */}
                <button
                  onClick={() => {
                    setShowRulesModal(true)
                  }}
                  className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>Advanced Rules</span>
                </button>
              </div>

              {/* Required Fields */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Required Fields</h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        Fields that must be filled before a deal can enter this status.
                        Enabling cascades forward to later statuses; disabling cascades backward to earlier ones.
                      </p>
                    </div>
                    {savingFields && (
                      <span className="text-xs text-gray-400 dark:text-gray-500 animate-pulse">Saving…</span>
                    )}
                  </div>
                </div>

                <div className="p-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {AVAILABLE_FIELDS.map((field) => {
                      const isChecked = requiredFields.includes(field.key)
                      // Hide deprecated fields that are already unchecked — they can't be re-enabled
                      if (field.deprecated && !isChecked) return null
                      return (
                        <label
                          key={field.key}
                          className={`flex items-start space-x-3 p-3 rounded-lg border cursor-pointer transition-all ${
                            field.deprecated
                              ? 'border-orange-300 bg-orange-50 dark:bg-orange-900/20 dark:border-orange-600'
                              : isChecked
                                ? 'border-purple-400 bg-purple-50 dark:bg-purple-900/20 dark:border-purple-500'
                                : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                          }`}
                        >
                          <div className="relative flex-shrink-0 mt-0.5">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => handleToggleField(field.key)}
                              disabled={savingFields}
                              className="sr-only"
                            />
                            <div className={`w-5 h-5 rounded flex items-center justify-center border-2 transition-colors ${
                              field.deprecated
                                ? 'bg-orange-500 border-orange-500'
                                : isChecked
                                  ? 'bg-purple-600 border-purple-600'
                                  : 'border-gray-300 dark:border-gray-500 bg-white dark:bg-gray-700'
                            }`}>
                              {isChecked && (
                                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                          </div>
                          <div className="min-w-0">
                            <p className={`text-sm font-medium ${
                              field.deprecated
                                ? 'text-orange-800 dark:text-orange-200 line-through'
                                : isChecked
                                  ? 'text-purple-900 dark:text-purple-200'
                                  : 'text-gray-800 dark:text-gray-200'
                            }`}>
                              {field.label}
                            </p>
                            <p className={`text-xs mt-0.5 ${field.deprecated ? 'text-orange-600 dark:text-orange-400 font-medium' : 'text-gray-500 dark:text-gray-400'}`}>
                              {field.description}
                            </p>
                          </div>
                        </label>
                      )
                    })}
                  </div>

                  {requiredFields.length > 0 && (
                    <div className="mt-4 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                      <p className="text-xs text-purple-700 dark:text-purple-300">
                        <strong>{requiredFields.length} field{requiredFields.length !== 1 ? 's' : ''} required</strong> — a deal cannot enter "{selectedStatus.name}" without these fields filled in.
                        {statuses.filter(s => s.position > selectedStatus.position).length > 0 && (
                          <span className="ml-1">Enabling cascades forward; disabling cascades backward.</span>
                        )}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
              <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <p className="text-gray-600 dark:text-gray-400 font-medium">Select a deal status</p>
              <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Choose a status on the left to configure its required fields and rules</p>
            </div>
          )}
        </div>
      </div>

      {/* ── Advanced Rules Modal ─────────────────────────────────────────────── */}
      {showRulesModal && selectedStatus && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between flex-shrink-0">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Advanced Rules — "{selectedStatus.name}"
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Automations and custom validations for this status
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => {
                    setEditingRule(null)
                    resetForm()
                    setShowCreateRule(true)
                  }}
                  className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-2 text-sm"
                >
                  <PlusIcon className="h-4 w-4" />
                  <span>Add Rule</span>
                </button>
                <button
                  onClick={() => { setShowRulesModal(false); setShowCreateRule(false); setEditingRule(null); resetForm() }}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <XIcon className="h-5 w-5 text-gray-500" />
                </button>
              </div>
            </div>

            {/* Rules list */}
            <div className="flex-1 overflow-y-auto p-6">
              {rulesLoading ? (
                <p className="text-center text-gray-500 py-8">Loading rules…</p>
              ) : rules.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500 dark:text-gray-400">No advanced rules configured</p>
                  <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Add automations or custom validations using the button above</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {rules.map((rule) => (
                    <div key={rule.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <h4 className="font-semibold text-gray-900 dark:text-white text-sm">{rule.name}</h4>
                            <span className={`px-2 py-0.5 text-xs rounded-full ${
                              rule.trigger_type === 'on_enter'
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                            }`}>
                              {rule.trigger_type === 'on_enter' ? 'On Enter' : 'On Exit'}
                            </span>
                            {!rule.is_active && (
                              <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                                Inactive
                              </span>
                            )}
                          </div>
                          {rule.description && (
                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{rule.description}</p>
                          )}
                          {rule.required_fields && rule.required_fields.length > 0 && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              <strong>Required:</strong> {rule.required_fields.join(', ')}
                            </p>
                          )}
                          {rule.automation_type && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                              <strong>Automation:</strong> {rule.automation_type}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center space-x-1 ml-4">
                          <button
                            onClick={() => handleToggleRule(rule)}
                            className={`px-2 py-1 text-xs rounded ${
                              rule.is_active
                                ? 'bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-400'
                            }`}
                          >
                            {rule.is_active ? 'Active' : 'Inactive'}
                          </button>
                          <button
                            onClick={() => openEditRule(rule)}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded transition-colors"
                          >
                            <EditIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteRule(rule.id)}
                            className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Create/Edit Rule Modal ───────────────────────────────────────────── */}
      {showCreateRule && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editingRule ? 'Edit Rule' : 'Create Rule'} — "{selectedStatus?.name}"
              </h3>
              <button
                onClick={() => { setShowCreateRule(false); setEditingRule(null); resetForm() }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
              >
                <XIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rule Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                  placeholder="e.g. Require funding details"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                  rows={2}
                  placeholder="Optional description"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Trigger</label>
                <div className="flex space-x-4">
                  <label className="flex items-center cursor-pointer">
                    <input type="radio" checked={formData.trigger_type === 'on_enter'} onChange={() => setFormData({ ...formData, trigger_type: 'on_enter' })} className="mr-2" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">On Enter</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input type="radio" checked={formData.trigger_type === 'on_exit'} onChange={() => setFormData({ ...formData, trigger_type: 'on_exit' })} className="mr-2" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">On Exit</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Required Fields (for this rule)</label>
                <div className="grid grid-cols-2 gap-2">
                  {AVAILABLE_FIELDS.filter(f => !f.deprecated).map((field) => (
                    <label key={field.key} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.required_fields.includes(field.key)}
                        onChange={() => toggleFormField(field.key)}
                        className="rounded"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{field.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Custom Validation</label>
                <select
                  value={formData.custom_validation}
                  onChange={(e) => setFormData({ ...formData, custom_validation: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                >
                  {customValidations.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Validation Message</label>
                <input
                  type="text"
                  value={formData.validation_message}
                  onChange={(e) => setFormData({ ...formData, validation_message: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                  placeholder="Message shown when validation fails"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Automation</label>
                <select
                  value={formData.automation_type}
                  onChange={(e) => setFormData({ ...formData, automation_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                >
                  {automationTypes.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Priority</label>
                <input
                  type="number"
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                  placeholder="Lower = higher priority"
                />
              </div>

              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Active</span>
              </label>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
              <button
                onClick={() => { setShowCreateRule(false); setEditingRule(null); resetForm() }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={editingRule ? handleUpdateRule : handleCreateRule}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center space-x-2"
              >
                <SaveIcon className="h-4 w-4" />
                <span>{editingRule ? 'Update' : 'Create'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default StatusRulesSection
